/**
 * "Family or cluster?" — the one question every learner asks of these filters.
 *
 * Answered in place, in plain words, with examples. A popover rather than a
 * hover tooltip so it works on a phone and from the keyboard: the trigger is a
 * real button, 44px tall, and the panel is described to screen readers.
 */
import { HelpCircle } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export default function FamilyClusterHelp({
  label = "What's the difference?",
  className = "",
}: {
  label?: string;
  className?: string;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="What's the difference between a career family and a cluster?"
          className={`inline-flex min-h-11 items-center gap-1.5 rounded-xl px-2 text-xs font-medium text-primary underline decoration-primary/40 underline-offset-4 transition-colors hover:decoration-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background ${className}`}
        >
          <HelpCircle className="h-3.5 w-3.5" aria-hidden="true" />
          {label}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[min(20rem,calc(100vw-2rem))] rounded-2xl text-sm leading-relaxed"
      >
        <h3 className="font-display text-sm font-bold">Family vs cluster</h3>
        <dl className="mt-2.5 space-y-2.5 text-muted-foreground">
          <div>
            <dt className="font-semibold text-foreground">Family — the kind of work</dt>
            <dd>
              What you'd actually do all day, and the skills you'd build. Software &amp; Data,
              Design &amp; Media, Healthcare.
            </dd>
          </div>
          <div>
            <dt className="font-semibold text-foreground">Cluster — the sector you'd do it in</dt>
            <dd>
              The industry around the job. One family shows up across many clusters — a data
              analyst works in health, in sport, in banking.
            </dd>
          </div>
        </dl>
        <p className="mt-3 border-t border-border/50 pt-2.5 text-xs text-muted-foreground">
          Pick a <span className="text-foreground/80">family</span> when you know the work you
          enjoy. Pick a <span className="text-foreground/80">cluster</span> when you know the world
          you want to be part of.
        </p>
      </PopoverContent>
    </Popover>
  );
}
